import { useState, useCallback, useRef } from 'react';
import { Mixpanel } from '../services/Mixpanel';

const MAX_IMAGES = 5;
const MAX_IMAGE_SIZE = 5 * 1000 * 1000; // 5MB
const VALID_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];

/**
 * Shared hook for uploading images to S3.
 *
 * @param {Object} options
 * @param {string} options.trackingPrefix - Mixpanel event prefix (e.g. 'Unlimited' or 'Unlimited Source Content')
 * @param {function} options.setError - Error setter (receives a string message)
 * @returns {Object} Image upload state and handlers
 */
export function useImageUpload({ trackingPrefix = 'Unlimited', setError } = {}) {
  const [imageFiles, setImageFiles] = useState([]);
  const [uploadedImageUrls, setUploadedImageUrls] = useState([]);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const imageInputRef = useRef(null);

  const handleImageUpload = useCallback(
    async (file) => {
      if (!file) return;

      // Use functional check to avoid stale closure issues with rapid uploads
      let alreadyAtMax = false;
      setImageFiles((prev) => {
        if (prev.length >= MAX_IMAGES) {
          alreadyAtMax = true;
          return prev;
        }
        return [...prev, file];
      });
      if (alreadyAtMax) {
        setError?.(`Maximum ${MAX_IMAGES} images allowed`);
        return;
      }

      if (file.size > MAX_IMAGE_SIZE) {
        setError?.('Please choose a smaller image: limit is 5MB');
        // Undo the optimistic add
        setImageFiles((prev) => prev.filter((f) => f !== file));
        return;
      }

      if (!VALID_IMAGE_TYPES.includes(file.type)) {
        setError?.('Please upload a valid image (PNG, JPG, GIF, or WebP)');
        setImageFiles((prev) => prev.filter((f) => f !== file));
        return;
      }

      setIsUploadingImage(true);
      setError?.(null);

      try {
        const { getSignedURL, uploadToS3 } = await import('../services/AWSService');
        const s3Info = await getSignedURL(file.name, file.type);
        await uploadToS3(s3Info.data.signed_url, file, file.type);
        setUploadedImageUrls((prev) => [...prev, s3Info.data.unsigned_url]);
        Mixpanel.track(`${trackingPrefix} Image Uploaded`, {
          file_name: file.name,
          file_size: file.size,
          file_type: file.type,
        });
      } catch (err) {
        console.error('Error uploading image:', err);
        setError?.('Failed to upload image. Please try again.');
        setImageFiles((prev) => prev.filter((f) => f !== file));
        Mixpanel.track(`${trackingPrefix} Image Upload Failed`, {
          error: err.message,
        });
      } finally {
        setIsUploadingImage(false);
      }
    },
    [trackingPrefix, setError]
  );

  const handleImageInputChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (file) {
        handleImageUpload(file);
      }
      e.target.value = '';
    },
    [handleImageUpload]
  );

  const handleImagePaste = useCallback(
    (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            handleImageUpload(file);
          }
          return;
        }
      }
      // If no image found, let default paste behavior proceed (text)
    },
    [handleImageUpload]
  );

  const clearImage = useCallback(
    (index) => {
      Mixpanel.track(`${trackingPrefix} Image Removed`, { image_index: index });
      setImageFiles((prev) => prev.filter((_, i) => i !== index));
      setUploadedImageUrls((prev) => prev.filter((_, i) => i !== index));
    },
    [trackingPrefix]
  );

  const clearAllImages = useCallback(() => {
    setImageFiles([]);
    setUploadedImageUrls([]);
  }, []);

  return {
    imageFiles,
    uploadedImageUrls,
    isUploadingImage,
    imageInputRef,
    maxImages: MAX_IMAGES,
    handleImageUpload,
    handleImageInputChange,
    handleImagePaste,
    clearImage,
    clearAllImages,
  };
}
